import { isoDay } from "./format";

const SHOP_API_URL = process.env.REACT_APP_SHOP_API_URL || "http://localhost:8000/api";

/**
 * Custom error that carries the HTTP status code, so callers can
 * distinguish "session expired" (401) from other failures.
 */
class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function parseErrorMessage(response) {
  try {
    const data = await response.json();
    return data.detail || data.message || `Request failed (${response.status})`;
  } catch {
    return `Request failed (${response.status})`;
  }
}

/**
 * One JSON call to the shop API. Every endpoint here is cookie-authenticated
 * and every one turns a non-2xx into an ApiError carrying the status, so that
 * lives in one place rather than being restated per endpoint.
 *
 * @param {string} path - beginning with a slash, e.g. "/admin/session"
 * @param {RequestInit} [options]
 */
async function request(path, options = {}) {
  const response = await fetch(`${SHOP_API_URL}${path}`, {
    credentials: "include",
    ...options,
  });

  if (!response.ok) {
    throw new ApiError(await parseErrorMessage(response), response.status);
  }

  return response.json();
}

export function login(password) {
  return request("/admin/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
}

export function checkSession() {
  return request("/admin/session");
}

export function logout() {
  return request("/admin/logout", { method: "POST" });
}

/**
 * Uploads a product CSV, then polls the batch's processing status.
 *
 * The backend enqueues one Cloud Task per product and returns immediately
 * with a batch_id — processing happens out-of-band, poll
 * GET /admin/batch/{batch_id}/status, which reflects results as each task
 * completes.
 *
 * onProgress is called with:
 *   { type: "uploading_file", progress: number }   — while the CSV itself uploads
 *   { type: "batch_status", total, results }        — once per poll
 *   { type: "complete", total, succeeded, failed, results, timedOut? } — final state
 *
 * If a product's task never reports back (e.g. it crashes or runs out of
 * memory instead of writing a result), polling would otherwise continue
 * forever since `done` never reaches `total`. To guard against that, once
 * maxPollMs has elapsed since the first poll, any products still missing
 * from `results` are treated as failed with a "timed out" error so the
 * batch always resolves.
 *
 * Resolves with the final "complete" event.
 *
 * @param {File} file
 * @param {(event: object) => void} onProgress
 * @param {{ pollIntervalMs?: number, maxPollMs?: number }} [options]
 */
export function uploadProducts(file, onProgress, { pollIntervalMs = 5000, maxPollMs = 10 * 60 * 1000 } = {}) {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${SHOP_API_URL}/admin/upload`, true);
    xhr.withCredentials = true;

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress({ type: "uploading_file", progress });
      }
    };

    xhr.onload = () => {
      let data = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        // non-JSON response, leave data as null
      }

      if (xhr.status >= 200 && xhr.status < 300 && data?.batch_id) {
        pollBatchStatus(data.batch_id, data.total, onProgress, pollIntervalMs, maxPollMs)
          .then(resolve)
          .catch(reject);
      } else {
        const message = data?.detail || data?.message || `Upload failed (${xhr.status})`;
        reject(new ApiError(message, xhr.status));
      }
    };

    xhr.onerror = () => {
      reject(new ApiError("Network error during upload", 0));
    };

    xhr.send(formData);
  });
}

async function pollBatchStatus(batchId, total, onProgress, pollIntervalMs, maxPollMs) {
  const startedAt = Date.now();

  while (true) {
    const batch = await request(`/admin/batch/${batchId}/status`);
    const results = { ...(batch.results || {}) };
    const done = Object.keys(results).length;

    if (onProgress) onProgress({ type: "batch_status", total: batch.total, results });

    const timedOut = Date.now() - startedAt >= maxPollMs;

    if (done >= total || timedOut) {
      // Any product still missing a result (e.g. its task crashed or ran
      // out of memory without ever reporting back) gets marked as failed
      // so the batch doesn't hang forever waiting for it.
      if (timedOut && done < total) {
        for (let i = 0; i < total; i++) {
          const key = String(i);
          if (!(key in results)) {
            results[key] = { ok: false, error: "Timed out waiting for result" };
          }
        }
      }

      const succeeded = Object.values(results).filter((r) => r.ok).length;
      const failed = total - succeeded;
      const finalEvent = { type: "complete", total, succeeded, failed, results, timedOut };
      if (onProgress) onProgress(finalEvent);
      return finalEvent;
    }

    await new Promise((r) => setTimeout(r, pollIntervalMs));
  }
}

// ── Payouts ─────────────────────────────────────────────────────────────

/**
 * Fetch vendors matching a search query (case-insensitive substring match).
 *
 * @param {string} [query]
 * @returns {Promise<{ id: string, name: string }[]>}
 */
export function getVendors(query = "") {
  const params = new URLSearchParams();
  if (query) params.set("q", query);

  return request(`/admin/payouts/vendors?${params}`);
}

/**
 * Fetch payout data for one vendor over a time range.
 *
 * @param {string} vendorId
 * @param {{ start: Date, end: Date }} range
 * @returns {Promise<{ summary: object, orders: object[] }>}
 */
export function getVendorPayouts(vendorId, { start, end }) {
  const params = new URLSearchParams({
    start: start.toISOString(),
    end: end.toISOString(),
  });

  return request(
    `/admin/payouts/vendors/${encodeURIComponent(vendorId)}?${params}`
  );
}

// ── Performance ─────────────────────────────────────────────────────────

/**
 * Fetch the trading table — site funnel, P&L and ad account per period.
 *
 * Rates come back as fractions (0.0067, not 0.67) and are null where the
 * denominator was zero, so the table can print a dash rather than a number
 * nobody should act on. `partial` marks weeks the requested range only partly
 * covers; days are never partial.
 *
 * Slower than the other endpoints — it makes four Shopify analytics queries
 * and a chunked Meta pull, so expect several seconds on a quarter. Day grain
 * over a single week is much cheaper, which is why expanding a row fetches
 * one week at a time instead of every day being pulled up front.
 *
 * @param {{ start: Date, end: Date, grain?: "week" | "day" }} params
 * @returns {Promise<{ grain: string, periods: object[] }>}
 */
export function getPerformance({ start, end, grain = "week" }) {
  const params = new URLSearchParams({
    start: isoDay(start),
    end: isoDay(end),
    grain,
  });

  return request(`/admin/performance?${params}`);
}

/**
 * The seven days inside one Monday-start week, for expanding a row.
 *
 * @param {string} weekIso - the week's Monday, as YYYY-MM-DD
 */
export function getWeekDays(weekIso) {
  const start = new Date(`${weekIso}T00:00:00`);
  const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
  return getPerformance({ start, end, grain: "day" });
}

// ── Product performance ─────────────────────────────────────────────────────

/**
 * Per-product trading: ad spend, traffic, funnel and revenue per destination.
 *
 * Rows come back at `grain` granularity with a `period` on each, and the client
 * groups them by `key`. One request therefore serves both the collapsed table
 * and every expanded row — unlike the store performance table, where day grain
 * is a separate and much more expensive query.
 *
 * Slower than the other endpoints: three ShopifyQL queries, a chunked Meta pull
 * at ad level, and a creative lookup for every ad that spent.
 *
 * @param {{ start: Date, end: Date, grain?: "week" | "day" }} params
 * @returns {Promise<{ grain: string, rows: object[] }>}
 */
export function getProductPerformance({ start, end, grain = "week" }) {
  const params = new URLSearchParams({
    start: isoDay(start),
    end: isoDay(end),
    grain,
  });

  return request(`/admin/products/performance?${params}`);
}
